"use client";

import { Activity, Brain, Compass, Flame, ChartLine as LineChart, Radar } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ShippingPolicyGrid() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const capabilities = [{
    number: "01",
    title: "Global Standard Logistics",
    description: "We partner with trusted international carriers to deliver your curated purchases promptly and securely right to your door.",
    Icon: LineChart
  }, {
    number: "02",
    title: "Priority Express Atelier",
    description: "Need your items quickly? Choose our expedited shipping service at checkout for rapid delivery on all in-stock styles.",
    Icon: Brain
  }, {
    number: "03",
    title: "International Concierge",
    description: "We ship worldwide to over 50 destinations, bringing contemporary fashion directly to international fashion enthusiasts.",
    Icon: Radar
  }, {
    number: "04",
    title: "Real-Time Transparency",
    description: "Stay fully informed at every step of transit with automated tracking details and live updates sent directly to you.",
    Icon: Compass
  }, {
    number: "05",
    title: "White-Glove Returns",
    description: "Enjoy peace of mind with our hassle-free 30-day return policy for unworn items, making exchanges simple and straightforward.",
    Icon: Activity
  }, {
    number: "06",
    title: "Complimentary Transit",
    description: "We are pleased to offer free standard shipping on all qualifying orders over $150 as a thank you for shopping with us.",
    Icon: Flame
  }];
  return <section data-section-id="4553" id="shippingpolicygrid" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-10">
      <div className="max-w-7xl mx-auto">
        
        <div ref={headerRef} className={`grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 mb-16 md:mb-20 transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <div className="lg:col-span-5">
            <p className="font-default text-xs tracking-widest uppercase text-primary mb-6">
              03 / Shipping & Returns
            </p>
            <h2 className="font-default font-semibold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-foreground">
              Shipping & Returns Policy
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pl-8 flex items-end">
            <p className="font-default text-base md:text-lg leading-relaxed text-muted-foreground/90 max-w-2xl">
              At Drip District, we strive to make your shopping experience effortless and transparent from checkout to unboxing. Our reliable shipping and customer-first return policies are designed to ensure your complete satisfaction every step of the way.
            </p>
          </div>
        </div>

        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {capabilities.map((cap, i) => {
          const Icon = cap.Icon;
          return <div data-index={i} key={cap.number} className="group relative bg-background border border-border/40 hover:border-primary/40 hover:bg-secondary/50 hover:scale-[1.02] transition-all duration-500 ease-out p-10 sm:p-12 flex flex-col" style={{
            aspectRatio: "4 / 5",
            minHeight: "420px"
          }}>
                
                <span className="font-default text-xs tracking-widest text-primary">
                  {cap.number}
                </span>

                
                <div className="relative flex items-center justify-start mt-10 mb-10">
                  <div aria-hidden="true" className="absolute -inset-6 rounded-lg bg-primary/15 blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500" />
                  <Icon strokeWidth={1} className="relative size-10 text-foreground/90 group-hover:text-primary transition-colors duration-500" />
                </div>

                
                <h3 className="font-default font-semibold text-2xl leading-snug text-foreground mb-4">
                  {cap.title}
                </h3>

                
                <p className="font-default text-[15px] leading-relaxed text-muted-foreground/80 mb-8">
                  {cap.description}
                </p>

                
                <div className="mt-auto">
                  <Link to="/ShopPage" className="inline-flex items-center gap-2 font-default text-xs tracking-widest uppercase text-foreground/80 hover:text-primary transition-colors duration-300">
                    <span className="relative">
                      <span className="bg-left-bottom bg-gradient-to-r from-primary to-primary bg-[length:0%_1px] bg-no-repeat group-hover:bg-[length:100%_1px] transition-[background-size] duration-500 ease-out pb-0.5">
                        Shop Now
                      </span>
                    </span>
                    <span aria-hidden="true">→</span>
                  </Link>
                </div>
              </div>;
        })}
        </div>

        
        <div className="mt-16 md:mt-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-10 border-t border-border">
          <p className="font-default text-sm text-muted-foreground/80 max-w-xl">
            Need further assistance? Our dedicated customer care team is available to ensure your experience remains seamless and satisfying.
          </p>
          <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-default text-xs tracking-widest uppercase rounded-lg shadow-lg hover:shadow-xl transition-all duration-300">
            <Link to="/CustomerCarePage">Contact Support</Link>
          </Button>
        </div>
      </div>
    </section>;
}